import { Component, OnDestroy, OnInit } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { NgbActiveModal, NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { IBlankslate, IToast, ToastService, ITableConfig } from 'codex-lib';
import { Subscription } from 'rxjs';
import { ModalIntentType } from 'src/app/enum/modal-intent-type.enum';
import { ParticipantTypeEnum } from 'src/app/enum/participant.enum';
import { NewEnvelopeSteps } from 'src/app/enum/new-envelope-steps';
import { NewEnvelopeService } from '../new-envelope/new-envelope.service';
import { TableResponsibleUser } from '../model/table-responsible-user';
import { Responsible } from 'src/app/model/responsible';
import { MainEnvelopeComponent } from 'src/app/main-envelope/main-envelope.component';
import { CPFCNPJUtil } from 'src/app/util/CPF-CNPJ-util';
import { StringUtil } from 'src/app/util/string-util';
import { EnvelopeDataTableService } from '../services/envelope-data-table.service';
import { AnalyticEventClient } from 'src/app/enum/analytic-event-client.enum';
import { TrackerData } from 'src/app/modules/google-analytics/model/tracker-data.model';
import { AnalyticsService } from 'src/app/modules/google-analytics/service/analytics.service';

@Component({
  selector: 'app-envelope-responsible',
  templateUrl: './envelope-responsible.component.html',
  styleUrls: ['./envelope-responsible.component.scss'],
})
export class EnvelopeResponsibleComponent implements OnInit, OnDestroy {
  tableResponsibleUser: TableResponsibleUser;
  blankSlate: IBlankslate;
  searchText = '';
  responsibleUsers: Subscription;
  stepModelSubscription: Subscription;
  checkboxAllElements: boolean;
  activeModal: NgbActiveModal;
  settings: any;
  hiddenPhoneColumn: boolean = false;
  modalIntentType = ModalIntentType;
  titleCasePipe = new TitleCasePipe();
  participantType = ParticipantTypeEnum;
  dataTableOptions: ITableConfig;

  model: {
    envelopeResponsible: Array<Responsible>;
    responsibleAccessAuthentication: boolean;
  };

  constructor(
    modalConfig: NgbModalConfig,
    private modalService: NgbModal,
    private newEnvelopeService: NewEnvelopeService,
    private translate: TranslateService,
    private toastService: ToastService,
    private mainEnvelopeComponent: MainEnvelopeComponent,
    private envelopeDataTableService: EnvelopeDataTableService,
    private googleAnalitycsService: AnalyticsService
  ) {
    this.model = {
      envelopeResponsible: new Array<Responsible>(),
      responsibleAccessAuthentication: false,
    };

    this.hiddenPhoneColumn = !this.mainEnvelopeComponent.isSendWhatsapp;

    this.tableResponsibleUser = new TableResponsibleUser();
    this.checkboxAllElements = false;
    this.responsibleUsers = this.tableResponsibleUser.responsibleUsers.subscribe((x) => {
      this.model.envelopeResponsible = x;
      this.model.envelopeResponsible.forEach((el) => {
        el.ssnWithMask = el.ssn ? CPFCNPJUtil.obfuscateCpfCnpj(CPFCNPJUtil.formatSsn(el.ssn)) : '';
        });
      this.model = this.newEnvelopeService.setModelData(this.model);
      this.newEnvelopeService.includeComponent = this.model.envelopeResponsible.length > 0;
      this.validateAllCheckboxCheckedTable();
    });

    this.stepModelSubscription = this.newEnvelopeService.stepModelSubject.subscribe((modelData) => {
      this.model = modelData;
    });

    modalConfig.backdrop = 'static';
    modalConfig.keyboard = false;
  }

  public trackById(index: number, item: Responsible) {
    return item?.id ?? index;
  }

  ngOnInit() {
    this.settings = this.newEnvelopeService.getModelData(NewEnvelopeSteps.Settings).value;

    Object.assign(this.model, this.newEnvelopeService.getModelData(this.newEnvelopeService.getCurrentStep()));
    this.tableResponsibleUser.addList(this.model.envelopeResponsible);
    this.newEnvelopeService.includeComponent = this.model.envelopeResponsible.length > 0;

    this.blankSlate = {
      icon: {
        prefix: 'fad',
        iconName: 'user-plus',
      },
      buttonText: 'add',
    };
    this.refreshDataTableOptions();
  };

  onToggleAccessAuthentication(event) {
    this.googleAnalitycsService.sendEvent(
      new TrackerData(
        AnalyticEventClient.TK_PT_SIGNER_NEW_ENVELOPE,
        'Signer_NovEnv_AutenticaçãoResponsavelForm',
        event ? 'Ativo' : 'Inativo'
      )
    );
    this.model.responsibleAccessAuthentication = event;
  }

  private refreshDataTableOptions(): void {
    const headers = this.envelopeDataTableService.buildHeader([
      { text: this.titleCasePipe.transform(this.translate.instant('name')), show: true },
      { text: this.titleCasePipe.transform(this.translate.instant('mail')), show: true },
      { text: this.translate.instant('ssn').toUpperCase(), show: this.settings?.language?.id === 1 },
      { text: this.translate.instant('phone'), show: !this.hiddenPhoneColumn },
    ]);
    const body = this.envelopeDataTableService.buildBody(this.model.envelopeResponsible.map((responsible) => ({
      row: [
        { display: responsible.name},
        { display: StringUtil.obfuscateMail(responsible.mail) || '-' },
        { display: responsible.ssnWithMask || '-' },
        { display: StringUtil.obfuscateTelephone(responsible.phoneNumber) || '-' },
      ],
      metaData: { isSelected: false },
    })));

    this.dataTableOptions = this.envelopeDataTableService.refreshDataTableOptions(headers, body, {
      isSelectable: false,
    });
  }

  ngOnDestroy(): void {
    this.responsibleUsers.unsubscribe();
    this.stepModelSubscription.unsubscribe();
  }

  confirmModalClose() {
    this.activeModal.dismiss();
  }

  confirmModalYesClick() {
    this.removeResponsible();
    this.activeModal.dismiss();
  }

  showConfirmationDelete(confirmModalContent) {
    this.activeModal = this.modalService.open(confirmModalContent);
  }

  removeResponsible() {
    const filterElements = Object.assign([], this.model.envelopeResponsible);
    filterElements.forEach((el) => {
      if (el[TableResponsibleUser.propCheck]) {
        this.tableResponsibleUser.removeEl(el);
      }
    });

    this.searchText = '';
    this.checkboxAllElements = false;
    this.newEnvelopeService.includeComponent = this.model.envelopeResponsible.length > 0;
    if (!this.newEnvelopeService.includeComponent) {
      this.newEnvelopeService.excludeAllCustomFields();
    }
  }

  validateAllCheckboxCheckedTable() {
    let allCheked = true;
    allCheked = this.model.envelopeResponsible.every((element) => element[TableResponsibleUser.propCheck]);
    if (allCheked || this.checkboxAllElements) {
      this.checkboxAllElements = allCheked;
    }
  }

  onCheckbox(ev, el) {
    const obj = {};
    obj[TableResponsibleUser.propCheck] = ev;
    this.tableResponsibleUser.updateElement(el, obj);
    this.searchText = '';
  }

  onCheckboxAll(ev) {
    this.checkboxAllElements = ev;
    const obj = {};
    obj[TableResponsibleUser.propCheck] = ev;
    this.tableResponsibleUser.updateElements(obj);
  }

  anyCheckedElement(): boolean {
    return this.model.envelopeResponsible.anyChecked(TableResponsibleUser.propCheck);
  }

  showAddResponsibleModal(modalContent) {
    this.activeModal = this.modalService.open(modalContent, { size: 'xl', scrollable: true });
  }

  closeModalClick() {
    this.activeModal.dismiss();
  }

  canAddResponsible(): boolean {
    return (this.model.envelopeResponsible?.length ?? 0) < 1;
  }

  submitAddModalClick(event: { data: any; isEdited: boolean }) {
    if (event.isEdited) {
      this.tableResponsibleUser.updateByElementId(event.data);
      this.newEnvelopeService.includeComponent = this.model.envelopeResponsible.length > 0;
      return;
    }

    if (!Array.isArray(event.data)) {
      return;
    }

    const firstResponsible = event.data[0];
    if (!firstResponsible) {
      return;
    }

    firstResponsible[TableResponsibleUser.propCheck] = false;
    this.tableResponsibleUser.addList([firstResponsible]);
    this.activeModal.close();
    this.newEnvelopeService.includeComponent = this.model.envelopeResponsible.length > 0;

    const toastUsersAdded: IToast = {
      text: this.translate.instant('envelope.responsible.toast.addedNotification', { count: 1 }),
      autoHide: true,
    };
    this.toastService.createNewToast(toastUsersAdded);
    this.refreshDataTableOptions();
  }
}
